/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import store from '@/store';
import * as api from '@/api/secret';
import { HttpResponseData } from '@/api/driver/http';
import { SecretCommentPost } from '@/api/types/secret';
import { SECRET, USER } from '@/store/types';
import { StoreDataCache, findCache, saveCache } from '@/store/cache';
import { finalizeAction, StoreActionEnv } from '@/store/actions';
import { updateObject } from '@/utils/util';

export interface StoreSecretCommentPostInfo {
  id: number;
  filter: string;
  last: number | undefined;
  lock: boolean;
  list: SecretCommentPost[];
  limit: number;
  point: number | undefined;
  height: number;
  noMore: boolean;
  extra: {
    partial: boolean;
    last: number;
  };
  updateTime: number;
}

const newCommentPostInfo = (id: number, filter: string, last: number): StoreSecretCommentPostInfo => ({
  lock: false,
  list: [],
  id,
  filter,
  last,
  limit: 10,
  point: void 0,
  height: 0,
  noMore: false,
  extra: {
    partial: false,
    last: 0,
  },
  updateTime: 0,
});

export interface StoreSecretCommentPostState {
  cache: StoreDataCache<StoreSecretCommentPostInfo>[];
  maxCache: number;
}

export default {
  namespaced: true,
  state: {
    cache: [],
    maxCache: 20,
  },
  getters: {},
  actions: {
    [SECRET.GET_COMMENT_POST](
      { commit, state }: StoreActionEnv<StoreSecretCommentPostState>,
      { action: rawAction, force, id, filter = '', last, silent },
    ) {
      const uid = store.state.user.user && store.state.user.user.id;
      const revision = store.state.secret.privateRooms.revision;
      let cache = findCache(state.cache, { id, filter, last });
      const action = finalizeAction(rawAction, cache, cache && cache.noMore && !force);
      if (!cache) {
        commit(SECRET.GET_COMMENT_POST, { status: 'new', id, filter, last });
        cache = findCache(state.cache, { id, filter, last });
      }
      if (action && cache) {
        const limit = cache.limit;
        const point = action === 'reload' ? void 0 : cache.point;
        commit(SECRET.GET_COMMENT_POST, { status: 'start', id, filter, last });
        return new Promise((resolve, reject) => {
          Promise.all([
            new Promise<HttpResponseData<SecretCommentPost[]>>((rsv, rej) => {
              api.getPostComments({
                id,
                filter,
                last: action === 'more' ? void 0 : last,
                point,
                limit,
                silent,
              }).then(rsv).catch(rej);
            }),
            store.dispatch(`secret/${SECRET.GET_EMOTIONS}`),
            // store.dispatch(`secret/topic/list/${SECRET.GET_TOPIC_LIST}`),
          ]).then(([res]) => {
            if (uid !== (store.state.user.user && store.state.user.user.id) || revision !== store.state.secret.privateRooms.revision) {
              throw new Error('内容权限已变化，请刷新');
            }
            commit(SECRET.GET_COMMENT_POST, {
              status: 'success',
              action,
              filter,
              point,
              id,
              last,
              list: res.data,
              extra: res.extra,
            });
            resolve();
          }).catch(() => {
            commit(SECRET.GET_COMMENT_POST, { status: 'failure', id, filter, last });
            reject();
          });
        });
      }
      return Promise.resolve();
    },
    [SECRET.POST_COMMENT_POST](
      { commit }: StoreActionEnv<StoreSecretCommentPostState>,
      { postId, body }: { postId: string; body: Parameters<typeof api.postCommentPost>[1] },
    ) {
      return new Promise((resolve, reject) => {
        api.postCommentPost(postId, body).then((res) => {
          commit(SECRET.POST_COMMENT_POST, { postId });
          store.commit(`user/${USER.UPDATE}`, {
            assign: { isDefaultFamous: body.isFamous },
          });
          resolve(res);
        }).catch((err) => {
          if (err.response && err.response.data && err.response.data.errcode === 404) {
            store.commit(`secret/list/${SECRET.POST_UPDATE}`, { id: postId, assign: { id: 0 } });
          }
          reject(err);
        });
      });
    },
  },
  mutations: {
    [SECRET.GET_COMMENT_POST](state: StoreSecretCommentPostState, { status, action, id, filter, last, point, list, extra }) {
      if (status === 'new') {
        const cache = newCommentPostInfo(id, filter, last);
        saveCache(state.cache, { id, filter, last }, cache, state.maxCache);
      } else if (status === 'start') {
        const cache = findCache(state.cache, { id, filter, last });
        if (cache) {
          cache.lock = true;
        }
      } else if (status === 'success') {
        const cache = findCache(state.cache, { id, filter, last }) || newCommentPostInfo(id, filter, last);
        if (!point) {
          cache.extra.last = extra.last;
          cache.extra.partial = extra.partial;
        }
        cache.list = action === 'reload' ? list : cache.list.concat(list);
        cache.noMore = list.length < cache.limit;
        cache.point = cache.list.length === 0 ? void 0 : cache.list[cache.list.length - 1].id;
        cache.filter = filter;
        cache.id = id;
        cache.last = last;
        cache.updateTime = new Date().valueOf();
        cache.lock = false;
        saveCache(state.cache, { id, filter, last }, cache, state.maxCache);
      }
    },
    [SECRET.POST_COMMENT_POST](state: StoreSecretCommentPostState, { postId }) {
      state.cache.forEach((item) => {
        if (item.id.includes(postId.toString())) {
          item.data.noMore = false;
        }
      });
    },
    [SECRET.COMMENT_UPDATE](state: StoreSecretCommentPostState, { id, updateInfo }) {
      state.cache.forEach((item) => {
        const cacheComment = item.data.list.find(p => p.id === id);
        if (cacheComment) {
          updateObject(cacheComment, updateInfo);
        }
      });
    },
    [SECRET.COMMENT_DEL_SUCCESS](state: StoreSecretCommentPostState, id) {
      state.cache.forEach((item) => {
        const index = item.data.list.findIndex(p => p.id === id);
        if (index >= 0) {
          item.data.list.splice(index, 1);
        }
      });
    },
  },
};
